import React, { useState, useEffect } from 'react';
import apiService from '../services/api';

const TrainerEarningsDashboard = ({ trainerId }) => {
  const [earningsData, setEarningsData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState(0);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [isStripeConnect, setIsStripeConnect] = useState(false);

  useEffect(() => {
    loadEarningsData();
    loadPayments();
  }, [trainerId, selectedYear]);

  const loadEarningsData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTrainerEarningsSummary(trainerId, selectedYear);
      
      if (response.Code === 200) {
        setEarningsData(response.data);
        if (response.source === 'stripe') {
          setIsStripeConnect(true);
        }
      } else {
        setError('Failed to load earnings data');
      }
    } catch (err) {
      console.error('Error loading earnings data:', err);
      setError('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      const response = await apiService.getTrainerPayments(trainerId);
      
      if (response.Code === 200) {
        setPayments(response.data);
        
        // Calculate pending payments
        const pending = response.data.filter(payment => payment.payment_status === 'pending');
        setPendingPayments(pending);
        setPayoutAmount(pending.reduce((sum, payment) => sum + payment.net_amount, 0));
      }
    } catch (err) {
      console.error('Error loading payments:', err);
    }
  };

  const handlePayoutRequest = async () => {
    try {
      setLoading(true);
      const response = await apiService.processTrainerPayout(trainerId, {
        payout_reference: `payout_${Date.now()}`
      });
      
      if (response.Code === 200) {
        alert('Payout request submitted successfully!');
        setShowPayoutModal(false);
        loadPayments();
        loadEarningsData();
      } else {
        setError(response.Message || 'Failed to process payout');
      }
    } catch (err) {
      console.error('Error processing payout:', err);
      setError('Failed to process payout');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return '#2ecc71';
      case 'pending': return '#f39c12';
      case 'failed': return '#e74c3c';
      default: return '#7f8c8d';
    }
  };

  const styles = {
    container: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      background: 'rgba(255, 255, 255, 0.95)',
      borderRadius: '15px',
      padding: '30px',
      maxWidth: '1200px',
      margin: '0 auto',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '30px',
      paddingBottom: '20px',
      borderBottom: '2px solid #ecf0f1'
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#2c3e50',
      margin: 0
    },
    yearSelector: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    yearSelect: {
      padding: '8px 12px',
      borderRadius: '8px',
      border: '2px solid #ddd',
      fontSize: '1rem',
      fontWeight: '600',
      color: '#2c3e50',
      background: 'white'
    },
    earningsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '20px',
      marginBottom: '30px'
    },
    earningsCard: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '25px',
      borderRadius: '15px',
      textAlign: 'center',
      boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
    },
    earningsCardTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      marginBottom: '10px',
      opacity: 0.9
    },
    earningsCardValue: {
      fontSize: '2.5rem',
      fontWeight: '700',
      marginBottom: '5px'
    },
    earningsCardSubtext: {
      fontSize: '0.9rem',
      opacity: 0.8
    },
    payoutSection: {
      background: 'rgba(46, 204, 113, 0.1)',
      border: '2px solid #2ecc71',
      borderRadius: '15px',
      padding: '25px',
      marginBottom: '30px',
      textAlign: 'center'
    },
    payoutTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#2c3e50',
      marginBottom: '15px'
    },
    payoutAmount: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#2ecc71',
      marginBottom: '20px'
    },
    payoutButton: {
      background: 'linear-gradient(45deg, #2ecc71, #27ae60)',
      color: 'white',
      border: 'none',
      padding: '15px 30px',
      borderRadius: '10px',
      fontSize: '1.1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(46, 204, 113, 0.3)'
    },
    paymentsTable: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '20px',
      background: 'white',
      borderRadius: '10px',
      overflow: 'hidden',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
    },
    tableHeader: {
      background: 'linear-gradient(45deg, #34495e, #2c3e50)',
      color: 'white',
      padding: '15px',
      textAlign: 'left',
      fontWeight: '600'
    },
    tableRow: {
      borderBottom: '1px solid #ecf0f1'
    },
    tableCell: {
      padding: '15px',
      fontSize: '0.9rem',
      color: '#2c3e50'
    },
    statusBadge: {
      padding: '5px 12px',
      borderRadius: '20px',
      fontSize: '0.8rem',
      fontWeight: '600',
      textTransform: 'uppercase'
    },
    error: {
      background: 'rgba(231, 76, 60, 0.1)',
      border: '1px solid #e74c3c',
      color: '#e74c3c',
      padding: '15px',
      borderRadius: '10px',
      marginBottom: '20px',
      textAlign: 'center'
    },
    loading: {
      textAlign: 'center',
      color: '#f39c12',
      fontSize: '1.1rem',
      padding: '40px'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      background: 'white',
      borderRadius: '15px',
      padding: '30px',
      maxWidth: '500px',
      width: '100%',
      textAlign: 'center'
    },
    modalTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#2c3e50',
      marginBottom: '20px'
    },
    modalText: {
      fontSize: '1.1rem',
      color: '#7f8c8d',
      marginBottom: '30px',
      lineHeight: '1.5'
    },
    modalButtons: {
      display: 'flex',
      gap: '15px',
      justifyContent: 'center'
    },
    modalButton: {
      padding: '12px 24px',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: 'none'
    },
    confirmButton: {
      background: 'linear-gradient(45deg, #2ecc71, #27ae60)',
      color: 'white'
    },
    cancelButton: {
      background: 'transparent',
      color: '#e74c3c',
      border: '2px solid #e74c3c'
    }
  };

  if (loading && !earningsData) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div>Loading earnings data...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Earnings Dashboard</h2>
        <div style={styles.yearSelector}>
          <label style={{ fontWeight: '600', color: '#2c3e50' }}>Year:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={styles.yearSelect}
          >
            {[2024, 2025, 2026].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Earnings Summary Cards */}
      <div style={styles.earningsGrid}>
        <div style={styles.earningsCard}>
          <div style={styles.earningsCardTitle}>Total Earnings ({selectedYear})</div>
          <div style={styles.earningsCardValue}>
            {formatCurrency(earningsData?.total_earnings || 0)}
          </div>
          <div style={styles.earningsCardSubtext}>
            {earningsData?.payment_count || 0} sessions
          </div>
        </div>

        <div style={styles.earningsCard}>
          <div style={styles.earningsCardTitle}>
            {isStripeConnect ? 'Available Balance' : 'Pending Payout'}
          </div>
          <div style={styles.earningsCardValue}>
            {isStripeConnect
              ? formatCurrency(earningsData?.available_balance || 0)
              : formatCurrency(payoutAmount)}
          </div>
          <div style={styles.earningsCardSubtext}>
            {isStripeConnect
              ? `${formatCurrency(earningsData?.pending_balance || 0)} pending`
              : `${pendingPayments.length} payments`}
          </div>
        </div>

        <div style={styles.earningsCard}>
          <div style={styles.earningsCardTitle}>First Payment</div>
          <div style={styles.earningsCardValue}>
            {earningsData?.first_payment_date ? 
              formatDate(earningsData.first_payment_date) : 'N/A'}
          </div>
          <div style={styles.earningsCardSubtext}>
            {selectedYear}
          </div>
        </div>

        <div style={styles.earningsCard}>
          <div style={styles.earningsCardTitle}>Last Payment</div>
          <div style={styles.earningsCardValue}>
            {earningsData?.last_payment_date ? 
              formatDate(earningsData.last_payment_date) : 'N/A'}
          </div>
          <div style={styles.earningsCardSubtext}>
            {selectedYear}
          </div>
        </div>
      </div>

      {/* Payout Section */}
      {isStripeConnect ? (
        <div style={{...styles.payoutSection, background: 'rgba(99, 91, 255, 0.1)', borderColor: '#635bff'}}>
          <h3 style={{...styles.payoutTitle, color: '#635bff'}}>Automatic Payouts</h3>
          <p style={{ color: '#7f8c8d', marginBottom: '10px' }}>
            Your earnings are automatically deposited to your connected bank account on Stripe's payout schedule.
          </p>
        </div>
      ) : payoutAmount > 0 ? (
        <div style={styles.payoutSection}>
          <h3 style={styles.payoutTitle}>Ready for Payout</h3>
          <div style={styles.payoutAmount}>
            {formatCurrency(payoutAmount)}
          </div>
          <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
            You have {pendingPayments.length} pending payment{pendingPayments.length !== 1 ? 's' : ''} ready for payout.
          </p>
          <button
            onClick={() => setShowPayoutModal(true)}
            style={styles.payoutButton}
            disabled={loading}
          >
            Request Payout
          </button>
        </div>
      ) : null}

      {/* Payments Table */}
      <div>
        <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#2c3e50', marginBottom: '20px' }}>
          Payment History
        </h3>
        <table style={styles.paymentsTable}>
          <thead>
            <tr>
              <th style={styles.tableHeader}>Session ID</th>
              <th style={styles.tableHeader}>Date</th>
              <th style={styles.tableHeader}>Gross Amount</th>
              <th style={styles.tableHeader}>Platform Fee</th>
              <th style={styles.tableHeader}>Net Amount</th>
              <th style={styles.tableHeader}>Status</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((payment) => (
              <tr key={payment.id} style={styles.tableRow}>
                <td style={styles.tableCell}>{payment.session_id}</td>
                <td style={styles.tableCell}>{formatDate(payment.created_at)}</td>
                <td style={styles.tableCell}>{formatCurrency(payment.gross_amount)}</td>
                <td style={styles.tableCell}>{formatCurrency(payment.platform_fee)}</td>
                <td style={styles.tableCell}>{formatCurrency(payment.net_amount)}</td>
                <td style={styles.tableCell}>
                  <span style={{
                    ...styles.statusBadge,
                    background: getStatusColor(payment.payment_status),
                    color: 'white'
                  }}>
                    {payment.payment_status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Payout Confirmation Modal */}
      {showPayoutModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>Confirm Payout Request</h3>
            <p style={styles.modalText}>
              You are requesting a payout of <strong>{formatCurrency(payoutAmount)}</strong> for {pendingPayments.length} pending payment{pendingPayments.length !== 1 ? 's' : ''}.
              <br /><br />
              This action will mark all pending payments as paid and generate a payout reference.
            </p>
            <div style={styles.modalButtons}>
              <button
                onClick={() => setShowPayoutModal(false)}
                style={{...styles.modalButton, ...styles.cancelButton}}
              >
                Cancel
              </button>
              <button
                onClick={handlePayoutRequest}
                style={{...styles.modalButton, ...styles.confirmButton}}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Confirm Payout'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainerEarningsDashboard;







